import { storage } from "@wlink/commons/core";
import { createWlinkI18n } from "@wlink/commons/i18n";
import axios from "axios";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { ElMessage } from "element-plus";
import {
  addXHeaders,
  loadAxios,
  QueryParseMode,
  sign,
} from "wikibroker-openapi-sdk";
import apis from "./modules/index.js";

// 扩展 dayjs 插件
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 转换时间字段为 UTC-0 格式
 * @param {Object} params - 请求参数对象
 * @returns {Object} 转换后的参数对象
 */
const convertTimeFieldsToUTC = (params) => {
  if (!params || typeof params !== "object") {
    return params;
  }

  const convertedParams = { ...params };
  const timeFields = ["start_time", "end_time", "exec_time"];

  timeFields.forEach((field) => {
    if (convertedParams[field]) {
      try {
        const localTime = dayjs(convertedParams[field]);
        if (localTime.isValid()) {
          // 转换为 UTC-0 时间的 RFC3339 格式
          convertedParams[field] = localTime
            .utc()
            .format("YYYY-MM-DDTHH:mm:ss[Z]");
        }
      } catch (error) {
        console.warn(
          `时间字段转换失败: ${field} = ${convertedParams[field]}`,
          error
        );
      }
    }
  });

  return convertedParams;
};

const i18n = createWlinkI18n();
const baseURL = typeof __API_URL__ !== "undefined" ? __API_URL__ : "";
axios.defaults.baseURL = baseURL;

/**
 * 设置axios默认的Authorization头
 * @param {string} token - JWT token
 */
export const setAuthorizationHeader = (token) => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
};

/**
 * 清除axios默认的Authorization头
 */
export const clearAuthorizationHeader = () => {
  delete axios.defaults.headers.common["Authorization"];
};

let errorQueue = []; // 错误信息队列
let isProcessingError = false; // 标记是否正在处理错误信息
let errorSet = new Set(); // 存储已经弹出的错误信息

/**
 * 请求拦截器
 */
axios.interceptors.request.use(
  async function (config) {
    config.timeout = 60000;
    const token = storage.get("token");
    if (token) {
      // 设置Authorization头，使用Bearer token格式
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    // 设置请求语言
    config.headers["X-Language"] = i18n.global.locale.value;
    // config.headers["x-portal"] = `client`;
    // 检查URL是否已经包含查询参数
    const hasQueryParams = config.url.includes("?");
    // 添加时间戳
    const timestamp = Date.now();
    config.url += `${hasQueryParams ? "&" : "?"}timestamp=${timestamp}`;

    // // 集成 wikibroker-openapi-sdk 签名（超管端不需要）
    if (
      typeof __APP_NAME__ !== "undefined" &&
      __APP_NAME__ !== "support-center"
    ) {
      try {
        const req = loadAxios(config, JSON.stringify, QueryParseMode.Bracket);
        addXHeaders(
          req.headers,
          __SDK_CLIENT_ID__, // 从环境变量配置
          new Date(),
          crypto.randomUUID()
        );
        await sign(req, __SDK_SECRET_KEY__); // 从环境变量配置
      } catch (error) {
        console.warn("SDK签名失败:", error);
      }
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

/**
 * 封装http请求函数
 * @param {请求名称} name
 * @param {接口入参} data
 * @param {接口路由} id
 */
const request = function (name = "", data = {}, id) {
  let {
    url,
    method = "post",
    headers = {
      "content-type": "application/json; charset=utf-8",
    },
    pem = false,
    pam = false, // 强制在URL中拼接参数
    sld = true,
    responseType,
  } = getApi(name);

  const item = {
    pem,
    sld,
    url: id ? `${url}/${id}` : url,
    headers,
    method,
    data,
    responseType,
  };
  if (
    pam ||
    item.method.toUpperCase() === "GET" ||
    item.method.toUpperCase() === "DELETE"
  ) {
    item.params = item.data;
  }

  // 转换时间字段为 UTC-0 格式
  if (item.params) {
    item.params = convertTimeFieldsToUTC(item.params);
  }
  if (item.data) {
    item.data = convertTimeFieldsToUTC(item.data);
  }

  function processErrorQueue() {
    if (errorQueue.length > 0 && !isProcessingError) {
      isProcessingError = true;
      const error = errorQueue[0]; // 获取队列中的第一个错误
      storage.clear();
      const errorMessage = i18n.global.t("65790");
      if (!errorSet.has(errorMessage)) {
        errorSet.add(errorMessage);
        ElMessage.error({
          message: errorMessage,
          duration: 3000,
          onClose: () => {
            location.href = "/login";
            storage.clear();
            errorQueue.shift(); // 处理完成后移除队列中的第一个错误
            errorSet.delete(errorMessage); // 从集合中删除已弹出的错误信息
            isProcessingError = false; // 重置标记
            setTimeout(processErrorQueue, 0); // 延迟处理错误信息队列中的下一个错误
          },
        });
      } else {
        errorQueue.shift(); // 处理完成后移除队列中的第一个错误
        isProcessingError = false; // 重置标记
        setTimeout(processErrorQueue, 0); // 延迟处理错误信息队列中的下一个错误
      }
    }
  }
  return new Promise((resolve, reject) => {
    axios(item)
      .then((resp) => {
        successHandler(resp, resolve, reject);
      })
      .catch((err) => {
        // 检查HTTP状态码401，自动跳转到登录页
        if ([401, 410].includes(err.response?.status)) {
          errorQueue.push(err); // 将错误信息添加到队列中
          if (!isProcessingError) {
            processErrorQueue(); // 处理错误信息队列
          }
          return; // 直接返回，不再执行后续的errorHandler
        }

        if (
          err.response?.data?.message === i18n.global.t("65791") ||
          err.response?.data?.message === i18n.global.t("65792")
        ) {
          errorQueue.push(err); // 将错误信息添加到队列中
          if (!isProcessingError) {
            processErrorQueue(); // 处理错误信息队列
          }
        }
        errorHandler(err.response, reject);
      });
  });
};

/**
 * 接口处理
 * @param resp
 * @param resolve
 */
function successHandler(resp, resolve, reject) {
  if (resp.request.responseType === "arraybuffer") {
    resolve(resp);
  } else {
    if (resp.data.code === 0 || resp.config.pem) {
      resolve(resp.data);
    } else if ([401, 402, 403, 410002].includes(resp.data.code)) {
      if (location.pathname !== "/login") {
        ElMessage.error({
          message: i18n.global.t("65790"),
          duration: 3000,
          onClose: () => {
            location.href = "/login";
            storage.clear();
          },
        });
      }
    } else {
      errorHandler(resp, reject);
    }
  }
}

/**
 * 错误接口处理
 * @param error
 */
function errorHandler(resp, reject) {
  const messageText =
    resp && resp.data
      ? `${resp.data.message || resp.data.msg || i18n.global.t("65793")}`
      : i18n.global.t("65793");
  ElMessage.error({
    message: messageText,
    duration: 3000,
  });
  reject(resp);
}

/**
 * 获取请求信息
 * @param {请求名称} name
 */
const getApi = function (name) {
  const api = apis.filter((v) => {
    return v.name === name;
  });
  if (api.length === 0) {
    throw new Error(`未找到${name}对应的API服务地址`);
  } else {
    return api[0];
  }
};

export default request;
